/* eslint-disable @typescript-eslint/no-explicit-any */
import { BodyText, Code, Layout, Tooltip } from '@postscript/components';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';

interface TokenBlockProps extends React.HTMLAttributes<HTMLElement> {
  name: string;
  variable: string;
  darkColor?: string;
  description?: string;
  example?: React.ReactNode;
  lightColor?: string;
  tight?: boolean;
}

const StyledTokenBlock = styled(Layout)<{ tight?: boolean }>`
  ${({ tight }) =>
    !tight &&
    css`
      border-top: 1px solid var(--border-color-dim);
      margin-top: var(--spacing-3);
      padding-top: var(--spacing-3);
    `}

  &:last-child {
    margin-bottom: var(--spacing-2);
  }
`;

const SwatchColor = styled.div<{ color: string }>`
  background: ${({ color }) => color};
  height: var(--spacing-4);
  width: var(--spacing-4);
  border-radius: var(--spacing-2);
  border: 1px solid var(--border-color-dim);
  font-size: 9px;
  line-height: var(--spacing-4);
  text-align: center;
  cursor: default;
`;

const StyledClickableCode = styled(Code)<{ onClick?: any }>`
  margin-bottom: var(--spacing-1);
  font: var(--body-text-x-small);
  cursor: pointer;
  display: block;

  &:hover {
    color: var(--link-color-hover);
  }

  &:active {
    color: var(--link-color-active);
  }
`;

const copyVariable = async (e: MouseEvent) => {
  try {
    await navigator.clipboard.writeText(
      `${(e.target as HTMLElement).dataset.copyvalue}`,
    );
    toast.success(`Copied: ${(e.target as HTMLElement).dataset.copyvalue}`);
  } catch (error) {
    toast.error('Could not copy color value :(');
  }
};

const TokenBlock = ({
  darkColor,
  description,
  example,
  lightColor,
  name,
  tight,
  variable,
  ...props
}: TokenBlockProps): JSX.Element => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);
  return (
    <StyledTokenBlock
      align="justify"
      gap="var(--spacing-3)"
      tight={tight}
      valign="top"
      {...props}
    >
      <div>
        <BodyText size="small">
          <strong>{name}</strong>
        </BodyText>
        <StyledClickableCode
          data-copyvalue={`var(--${variable})`}
          onClick={copyVariable}
        >
          var(--{variable})
        </StyledClickableCode>
        {description && <BodyText size="x-small">{description}</BodyText>}
      </div>
      <Layout gap="var(--spacing-1)">
        {lightColor && (
          <SwatchColor
            color={lightColor}
            data-for={`${variable}-tooltip`}
            data-tip
          />
        )}
        {darkColor && (
          <SwatchColor
            color={darkColor}
            data-for={`${variable}-dark-tooltip`}
            data-tip
          >
            🌙
          </SwatchColor>
        )}
        {example}
      </Layout>
      {lightColor && isMounted && (
        <Tooltip id={`${variable}-tooltip`} size="small">
          {lightColor}
        </Tooltip>
      )}
      {darkColor && isMounted && (
        <Tooltip id={`${variable}-dark-tooltip`} size="small">
          {darkColor}
        </Tooltip>
      )}
    </StyledTokenBlock>
  );
};

export default TokenBlock;
